import { Field } from "@/schemas/GetFieldsResponse"
import Icon, { UploadOutlined } from "@ant-design/icons";
import { Button, DatePicker, DatePickerProps, Flex, Input, InputRef, message, Typography, Upload, UploadFile, UploadProps } from "antd";
import { CSSProperties, ReactElement, useEffect, useRef, useState } from "react";
import EditIcon from "./EditIcon";
import styled from "styled-components";
import dayjs from "dayjs";

const { Text } = Typography;

type FormInputProps = Field & {
  value: string;
  setFile?: (file: File) => void;

  // If missing, field will be read-only
  onChange?: (value: string) => void;
};


const StyledInput = styled(Input)`
  & span > input {
    border: 0;
    padding-left: 46px;
  }
  & span > .ant-input-group-addon {
    border: 0;
  }
  & :focus {
    border: 0;
    box-shadow: none;
  }
`;

const CustomInputFieldWrapper = styled.div<{ isFocused: boolean }>`
  box-shadow: ${({ isFocused }) => isFocused ? "0 0 0 2px rgba(0, 9, 33, 0.74)" : "none"};
  transition: border 0.2s, box-shadow 0.2s, background 0.2s;
  border-radius: 16px;
`;

const StyledDatePicker = styled(DatePicker)`
  cursor: pointer;
  & div > input {
    text-align: center;
    margin-left: 18px;
  }
`

const StyledLabel = styled(Text)`
  font-weight: 900;
  font-size: 18px;
`;

const styles: { [key: string]: CSSProperties } = {
  input: {
    textAlign: "center",
  },
  dateinput: {
    width: "100%",
    border: "0"
  }
}

export default function FormInput(props: FormInputProps) {
  const [messageApi, contextHolder] = message.useMessage();
  const [isFocused, setIsFocused] = useState(false);
  const [fileName, setFileName] = useState("");
  const fieldRef = useRef<any>(null);

  useEffect(() => {
    if (props.prefilledValue && props.onChange) {
      props.onChange(props.prefilledValue);
    }
  }, [props.prefilledValue]);

  function handleUpdateDate(dateString: string) {
    if (props.onChange) {
      props.onChange(dateString);
    }
  }

  function handleIconClick() {
    fieldRef.current?.focus();
  }

  const uploadProps: UploadProps = {
    accept: props.type.startsWith("upload") ? props.type.substring(7) : undefined,
    beforeUpload: (file) => {
      return false;
    },
    onChange: (file) => {
      console.log("uploaded file", file);
      const f = file.file as unknown as File;
      const acceptableExts = props.type.startsWith("upload")
        ? props.type.substring(7).split(",")
        : undefined;
      if (props.setFile === undefined || acceptableExts === undefined) {
        console.error("Failed to set file for " + props.name);
        return;
      }
      try {
        const parts = f.name.split(".");
        const ext = "." + parts[parts.length - 1];
        if (!acceptableExts.includes(ext)) {
          messageApi.error("Only " + acceptableExts.join(", ") + " files are allowed");
          return;
        }
        props.setFile(f);
        setFileName(f.name);
      } catch (ex) {
        console.error("Failed to set file for " + props.name, ex);
        messageApi.error("Only " + acceptableExts.join(", ") + " files are allowed");
      }
    },
    showUploadList: false
  }

  return (<>
    {contextHolder}
    { (props.type === "text" || props.type === "email" || props.type === "number") &&
      <CustomInputFieldWrapper isFocused={isFocused}>
        <StyledInput
          placeholder={isFocused ? "" : props.label}
          addonAfter={props.onChange && <Icon onClick={handleIconClick} component={() => <EditIcon />} />}
          style={styles.input}
          type={props.type}
          required={props.required}
          maxLength={props.maxLength}
          readOnly={!props.onChange}
          className="custom-input-field"
          size="large"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          value={props.value}
          onChange={(e) => props.onChange && props.onChange(e.target.value)}
          ref={fieldRef}
        />
      </CustomInputFieldWrapper>
    }
    { props.type === "date" &&
      <StyledDatePicker
        size="large"
        className="FormDatePicker"
        style={styles.dateinput}
        required={props.required}
        placeholder={isFocused ? "" : props.label}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        suffixIcon={<Icon onClick={handleIconClick} component={() => <EditIcon />} />}
        value={props.value ? dayjs(props.value) : null}
        onChange={(date, dateString) => handleUpdateDate(dateString as string)}
        ref={fieldRef}
      />
    }
    { props.type.startsWith("upload") &&
      <Flex justify="space-between" align="center">
        <StyledLabel>{props.label}</StyledLabel>
        <Flex gap={8} align="center">
          <Typography.Text>{fileName}</Typography.Text>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Flex>
      </Flex>
    }
  </>)
}